/**
 * <s-pair-variant> — Option picker for a "Pairs well with" quick-add form.
 *
 * Markup:
 *   input[name="id"]                      — hidden variant id the form submits.
 *   script[data-pair-variants]            — JSON [{id, available, options[], image, price, compare}].
 *   fieldset[data-pair-option="<index>"]  — one per option, radio inputs whose value is the option value.
 *   [data-pair-thumb] img                 — thumbnail swapped to the chosen variant's photo.
 *   [data-pair-price]                     — price text updated when the variant's price differs.
 *   [data-pair-select]                    — legacy <select> of variant ids (cart drawer / cart page).
 * Sold-out combinations are faded ([data-unavailable] on the label); picking one jumps to the
 * closest in-stock variant that keeps the value just picked.
 */
class SPairVariant extends HTMLElement {
  #controller = null;
  #variants = [];

  connectedCallback() {
    const input = this.querySelector('input[name="id"]');
    if (!input) return;
    this.#controller = new AbortController();
    const signal = this.#controller.signal;

    const select = this.querySelector('[data-pair-select]');
    if (select) {
      input.value = select.value;
      select.addEventListener('change', () => { input.value = select.value; }, { signal });
    }

    const data = this.querySelector('script[data-pair-variants]');
    if (!data) return;
    try { this.#variants = JSON.parse(data.textContent); } catch { this.#variants = []; }
    if (!this.#variants.length) return;

    this.addEventListener('change', (event) => {
      const fieldset = event.target.closest?.('[data-pair-option]');
      if (!fieldset) return;
      this.#choose(Number(fieldset.dataset.pairOption), event.target.value);
    }, { signal });

    this.#refreshAvailability();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #selected() {
    return [...this.querySelectorAll('[data-pair-option]')].map((fs) => fs.querySelector('input:checked')?.value ?? null);
  }

  #choose(index, value) {
    const wanted = this.#selected();
    wanted[index] = value;
    const score = (v) => v.options.reduce((n, opt, i) => n + (opt === wanted[i] ? 1 : 0), 0);
    const candidates = this.#variants.filter((v) => v.options[index] === value);
    const pool = candidates.filter((v) => v.available);
    const best = (pool.length ? pool : candidates).sort((a, b) => score(b) - score(a))[0];
    if (best) this.#apply(best);
  }

  #apply(variant) {
    const input = this.querySelector('input[name="id"]');
    if (input) input.value = String(variant.id);

    this.querySelectorAll('[data-pair-option]').forEach((fs) => {
      const value = variant.options[Number(fs.dataset.pairOption)];
      fs.querySelectorAll('input').forEach((radio) => { radio.checked = radio.value === value; });
    });

    const img = this.querySelector('[data-pair-thumb] img');
    if (img && variant.image && img.getAttribute('src') !== variant.image) {
      img.removeAttribute('srcset');
      img.src = variant.image;
      img.animate?.([{ opacity: 0.4 }, { opacity: 1 }], { duration: 250, easing: 'ease-out' });
    }
    const form = this.closest('s-product-form');
    if (form && variant.image) form.dataset.flyImage = variant.image;

    const price = this.querySelector('[data-pair-price]');
    const prices = new Set(this.#variants.map((v) => `${v.price}|${v.compare}`));
    if (price && prices.size > 1) {
      price.replaceChildren();
      const now = document.createElement('span');
      now.className = 'text-sm text-on-surface';
      now.textContent = variant.price;
      price.append(now);
      if (variant.compare) {
        const was = document.createElement('s');
        was.className = 'ms-1.5 text-sm text-on-surface-secondary';
        was.textContent = variant.compare;
        price.append(was);
      }
    }

    const add = this.querySelector('button[name="add"]');
    if (add) {
      add.disabled = !variant.available;
      add.toggleAttribute('data-disabled', !variant.available);
    }

    this.#refreshAvailability();
  }

  // Fade values that have no in-stock variant with the other options as currently chosen.
  #refreshAvailability() {
    const chosen = this.#selected();
    this.querySelectorAll('[data-pair-option]').forEach((fs) => {
      const index = Number(fs.dataset.pairOption);
      fs.querySelectorAll('input').forEach((radio) => {
        const ok = this.#variants.some((v) => v.available && v.options[index] === radio.value
          && v.options.every((opt, i) => i === index || chosen[i] == null || opt === chosen[i]));
        radio.closest('label')?.toggleAttribute('data-unavailable', !ok);
      });
    });
  }
}

if (!customElements.get('s-pair-variant')) customElements.define('s-pair-variant', SPairVariant);
